import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Globe } from "lucide-react";
import { useLanguageWithOverride, Language } from "@/hooks/use-browser-language";
import { getPrivacyTranslation } from "@/i18n/privacy-policy";

const PrivacyPolicy = () => {
  const { language, setLanguage } = useLanguageWithOverride();
  
  const t = (key: Parameters<typeof getPrivacyTranslation>[1]) => 
    getPrivacyTranslation(language, key);

  const toggleLanguage = () => {
    const newLang: Language = language === "pl" ? "en" : "pl";
    setLanguage(newLang);
    const url = new URL(window.location.href);
    url.searchParams.set("lang", newLang);
    window.history.replaceState({}, "", url.toString());
  };

  return (
    <div className="min-h-screen gradient-subtle py-12">
      <div className="container px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="gradient-card border-none shadow-xl">
            <CardHeader>
              <CardTitle className="text-3xl font-bold mb-2">{t("title")}</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-slate max-w-none">
              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">{t("section1Title")}</h2>
                <p className="text-muted-foreground leading-relaxed">
                  {t("section1Text1")}
                </p>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  {t("section1Text2")}
                </p>
              </section>
              
              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">{t("section2Title")}</h2>
                <p className="text-muted-foreground leading-relaxed">
                  {t("section2Text")}
                </p>
              </section>
              
              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">{t("section3Title")}</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  {t("section3Text")}
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 leading-relaxed">
                  <li>{t("section3Item1")}</li>
                  <li>{t("section3Item2")}</li>
                  <li>{t("section3Item3")}</li>
                </ul>
              </section>
              
              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">{t("section4Title")}</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  {t("section4Text")}
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 leading-relaxed">
                  <li>{t("section4Item1")}</li>
                  <li>{t("section4Item2")}</li>
                  <li>{t("section4Item3")}</li>
                  <li>{t("section4Item4")}</li>
                  <li>{t("section4Item5")}</li>
                </ul>
              </section>
              
              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">{t("section5Title")}</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  {t("section5Text")}
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 leading-relaxed">
                  <li>{t("section5Item1")}</li>
                  <li>{t("section5Item2")}</li>
                  <li>{t("section5Item3")}</li>
                </ul>
              </section>
              
              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">{t("section6Title")}</h2>
                <p className="text-muted-foreground leading-relaxed">
                  {t("section6Text")}
                </p>
              </section>
              
              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">{t("section7Title")}</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  {t("section7Text")}
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 leading-relaxed">
                  <li>{t("section7Item1")}</li>
                  <li>{t("section7Item2")}</li>
                  <li>{t("section7Item3")}</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-3">
                  {t("section7Text2")}
                </p>
              </section>
              
              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">{t("section8Title")}</h2>
                <p className="text-muted-foreground leading-relaxed">
                  {t("section8Text")}
                </p>
              </section>
              
              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">{t("section9Title")}</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  {t("section9Text")}
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2 leading-relaxed">
                  <li>{t("section9Item1")}</li>
                  <li>{t("section9Item2")}</li>
                  <li>{t("section9Item3")}</li>
                  <li>{t("section9Item4")}</li>
                  <li>{t("section9Item5")}</li>
                  <li>{t("section9Item6")}</li>
                  <li>{t("section9Item7")}</li>
                  <li>{t("section9Item8")}</li>
                </ul>
                <p className="text-muted-foreground leading-relaxed mt-4">
                  {t("section9Text2")}
                </p>
              </section>
              
              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">{t("section10Title")}</h2>
                <p className="text-muted-foreground leading-relaxed">
                  {t("section10Text")}
                </p>
              </section>
              
              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">{t("section11Title")}</h2>
                <p className="text-muted-foreground leading-relaxed">
                  {t("section11Text")}
                </p>
              </section>

              {/* Language Switcher */}
              <div className="flex justify-center pt-4 border-t border-border/40">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleLanguage}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
                >
                  <Globe className="h-4 w-4" />
                  <span className="font-medium">{language === "pl" ? "English" : "Polski"}</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
